/* =========================================================================
   Monastery restoration (§9) — the gallery reframed as a chapel you rebuild.

   The chapel HEADER shows a plain % restored (owned pieces / catalog size) —
   2026-08-04, Eric: the old "Stage 2 / 3" badge "wasn't adding anything" and
   confused players who couldn't tell if the number meant progress or a rank.
   A percentage + "X of Y restored" fraction reads unambiguously as progress.
   Header ART still switches across 4 states (chapelStage: 0 items=stage0,
   1–2=1, 3–4=2, 5+=3) — that bucketing is invisible now, just picking which
   of the 4 background images/gradients to show; it no longer drives any text.

   The item grid comes from GET /api/meta/gallery (anonymous — guests browse with
   prices visible): owned pieces render lit (nameplate shown if any), purchasable
   pieces show price + wallet context. Purchase uses the authed
   /api/gallery/purchase with the optional rosary-bead discount (📿 −$1,000 each).
   On success Theo explains the piece via a 'gallery:<key>' line and fires
   GalleryPurchased{key} — which feeds the Pilgrimage 'Restore the Altar' gate.
   GUESTS never fake-purchase: the button is an honest sign-in nudge (wallet is
   server-side, accounts only).
   ========================================================================= */
import { esc, fmtCents } from './engine';
import { icon } from './ui/icons';
import { byId, show } from './dom';
import { track } from './telemetry';
import { getLine } from './characters/registry';
import { triggerCharPop } from './characters/ui';
import { showRegister } from './auth';
import {
  isAuthenticated,
  getGallery,
  getMyCollection,
  purchaseGallery,
  getProfileMe,
  type GalleryItem,
  type OwnedGalleryItem,
} from './api';
import { getQty, spendItem } from './inventory';
import { hasOptionalMedia } from './featureFlags';
import { loadWings, renderWings, resetWings, seedWingsWallet } from './monasteryWings';
import { openPoorBox } from './almsgiving';

const BEAD_CENTS = 100_000; // each rosary bead discounts $1,000 (§3 sink)

let catalog: GalleryItem[] = [];
let owned: Map<string, OwnedGalleryItem> = new Map();
let walletCents: number | null = null;
let useBeads = false;

/** PURE: restoration stage from the count of owned pieces. */
export function chapelStage(ownedCount: number): 0 | 1 | 2 | 3 {
  if (ownedCount >= 5) return 3;
  if (ownedCount >= 3) return 2;
  if (ownedCount >= 1) return 1;
  return 0;
}

/* ------------------------------ media probe ------------------------------ */
const stageArt: boolean[] = [false, false, false, false];
function probeChapelArt(): void {
  if (!hasOptionalMedia()) return; // chapel-stage*.png not installed yet — CSS gradient fallback
  for (let s = 0; s < 4; s++) {
    const img = new Image();
    const stage = s;
    img.onload = () => {
      stageArt[stage] = true;
      if (isVisibleMon()) renderChapel();
    };
    img.onerror = () => {};
    img.src = `media/chapel-stage${s}.png`;
  }
}

function isVisibleMon(): boolean {
  try {
    return !byId('screenMonastery').classList.contains('hidden');
  } catch {
    return false;
  }
}

/* -------------------------------- rendering -------------------------------- */
function renderChapel(): void {
  const el = document.getElementById('monChapel');
  if (!el) return;
  const stage = chapelStage(owned.size);
  const art = stageArt[stage]
    ? `<img class="mon-chapel-art" src="media/chapel-stage${stage}.png" alt="Chapel restoration stage ${stage}" />`
    : `<div class="mon-chapel-fallback stage-${stage}" aria-hidden="true">${icon('church', 64)}</div>`;
  const total = catalog.length;
  const pct = total > 0 ? Math.round((owned.size / total) * 100) : 0;
  const remaining = Math.max(0, total - owned.size);
  const fraction =
    total > 0 ? `${owned.size} of ${total} restored${remaining > 0 ? ` · ${remaining} to go` : ''}` : 'Loading…';
  el.innerHTML =
    art +
    `<div class="mon-chapel-overlay">` +
    `<div class="mon-stage-badge">${pct}% Restored</div>` +
    `<div class="mon-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}" aria-label="${pct}% of the chapel restored">` +
    `<span class="mon-bar-fill" style="width:${pct}%"></span>` +
    `</div>` +
    `<div class="mon-stage-caption">${esc(fraction)}</div>` +
    `</div>`;
}

function walletContext(): string {
  if (!isAuthenticated()) return '';
  if (walletCents == null) return `<div class="mon-wallet">Loading your wallet…</div>`;
  return `<div class="mon-wallet">${icon('coin', 16)} ${fmtCents(walletCents)} in your wallet</div>`;
}

function beadToggle(): string {
  const beads = getQty('rosary_bead');
  if (!isAuthenticated() || beads <= 0) return '';
  return (
    `<label class="mon-bead-toggle">` +
    `<input type="checkbox" id="monBeadChk" ${useBeads ? 'checked' : ''}/> ` +
    `${icon('rosary', 16)} Use ${beads} bead${beads === 1 ? '' : 's'} (−${fmtCents(BEAD_CENTS)} each)` +
    `</label>`
  );
}

function itemCard(item: GalleryItem): string {
  const own = owned.get(item.key);
  const ic = item.icon ? esc(item.icon) : icon('church', 22);
  if (own) {
    const plate = own.namePlate ? `<div class="mon-plate">“${esc(own.namePlate)}”</div>` : '';
    return (
      `<div class="mon-item owned">` +
      `<span class="mi-ic">${ic}</span>` +
      `<div class="mi-main"><div class="mi-name">${esc(item.name)}</div>` +
      `<div class="mi-owned-lbl">✓ In the chapel</div>${plate}</div>` +
      `</div>`
    );
  }
  const affordable = isAuthenticated() && walletCents != null && walletCents >= item.priceCents;
  const btn = isAuthenticated()
    ? `<button class="btn mi-buy${affordable ? '' : ' secondary'}" type="button" data-key="${esc(item.key)}">Restore · ${fmtCents(item.priceCents)}</button>`
    : `<button class="btn secondary mi-buy" type="button" data-key="${esc(item.key)}" data-nudge="1">${fmtCents(item.priceCents)} · Sign in to restore</button>`;
  return (
    `<div class="mon-item">` +
    `<span class="mi-ic">${ic}</span>` +
    `<div class="mi-main"><div class="mi-name">${esc(item.name)}</div>` +
    (item.descr ? `<div class="mi-descr">${esc(item.descr)}</div>` : '') +
    btn +
    `</div></div>`
  );
}

function renderBody(): void {
  const body = document.getElementById('monBody');
  if (!body) return;
  const note = byId('monNote');
  if (!isAuthenticated()) {
    note.classList.remove('hidden');
    note.textContent = 'Browse the whole chapel as a guest, prices and all. Purchases use your wallet, so create a free account to begin restoring.';
  } else {
    note.classList.add('hidden');
  }
  body.innerHTML =
    walletContext() +
    beadToggle() +
    `<div class="mon-grid">${catalog.map(itemCard).join('')}</div>`;

  const chk = document.getElementById('monBeadChk') as HTMLInputElement | null;
  if (chk) chk.onchange = () => { useBeads = chk.checked; };
  body.querySelectorAll<HTMLButtonElement>('.mi-buy').forEach((b) => {
    b.onclick = () => {
      if (b.dataset.nudge) {
        byId('bankbar').classList.add('hidden');
        showRegister();
      } else {
        void buy(b.dataset.key || '');
      }
    };
  });
}

function renderAll(): void {
  renderChapel();
  renderBody();
  renderWings(); // db/047 — the expansion ladder below the gallery grid
}

/* -------------------------------- purchase -------------------------------- */
function toast(msg: string, kind: 'ok' | 'err' = 'ok'): void {
  const t = byId('monToast');
  t.textContent = msg;
  t.className = 'acct-toast ' + kind;
  void t.offsetWidth;
  t.classList.add('show');
  setTimeout(() => {
    t.classList.remove('show');
    t.classList.add('hidden');
  }, 3000);
}

async function buy(key: string): Promise<void> {
  const item = catalog.find((c) => c.key === key);
  if (!item) return;
  // Never send more beads than the item can absorb — the server caps consumption
  // the same way (019's @maxUseful), and burning surplus beads locally would
  // vaporize them. min(held, ceil(price / bead value)).
  const beads = useBeads
    ? Math.min(getQty('rosary_bead'), Math.max(1, Math.ceil((item.priceCents || 0) / BEAD_CENTS)))
    : 0;
  const r = await purchaseGallery({ galleryItemKey: key, beadsToSpend: beads });
  if (!r.ok) {
    toast(r.offline ? 'Offline. Try again when connected.' : r.error || 'Could not complete the purchase.', 'err');
    return;
  }
  // Success: reflect the new wallet, spend beads locally to match the server,
  // advance ownership + stage, fire telemetry, and let Theo explain the piece.
  walletCents = r.data.walletBalanceCents ?? walletCents;
  if (beads > 0) spendItem('rosary_bead', beads, 'gallery_discount');
  useBeads = false;
  owned.set(key, {
    collectionId: r.data.ledgerEntryId,
    key,
    name: item.name,
    icon: item.icon,
    namePlate: null,
    purchasedUtc: new Date().toISOString(),
    displayOrder: 100,
  });
  track('GalleryPurchased', { key }); // metric ItemKey → props.key (pilgrimage gate)
  renderAll();
  try {
    triggerCharPop(getLine('theo', `gallery:${key}`));
  } catch {
    /* no DOM */
  }
  toast('Restored. The chapel is a little more beautiful.');
}

/* -------------------------------- loading -------------------------------- */
async function load(): Promise<void> {
  const [cat, mine, prof] = await Promise.all([
    getGallery(),
    getMyCollection(),
    isAuthenticated() ? getProfileMe() : Promise.resolve(null),
    loadWings(), // db/047 — fetched in parallel so the ladder paints with the grid
  ]);
  if (cat.ok && Array.isArray(cat.data)) catalog = cat.data;
  owned = new Map();
  if (mine.ok && Array.isArray(mine.data)) for (const o of mine.data) owned.set(o.key, o);
  if (prof && prof.ok) walletCents = prof.data.stats?.walletBalanceCents ?? 0;
  seedWingsWallet(walletCents);
}

/* -------------------------------- public -------------------------------- */
export async function openMonastery(): Promise<void> {
  track('AreaOpened', { area: 'monastery' });
  byId('bankbar').classList.add('hidden');
  show('monastery');
  renderAll(); // instant paint (last-known / empty)
  await load();
  renderAll();
}

export function initMonastery(): void {
  probeChapelArt();
  byId('monBackBtn').addEventListener('click', () => {
    byId('bankbar').classList.add('hidden');
    show('home');
  });

  // The Poor Box (db/047). A deliberate, player-opened surface — never an
  // interstitial, so it does not go through the Moment Director. Guests get the
  // same honest sign-in nudge the gallery gives: the wallet is accounts-only.
  byId('monAlmsBtn').addEventListener('click', () => {
    if (!isAuthenticated()) {
      byId('bankbar').classList.add('hidden');
      showRegister();
      return;
    }
    void openPoorBox().then(() => {
      // Giving moves the wallet, so the gallery grid's affordability is stale.
      void refreshWalletAfterGiving();
    });
  });
}

/** Re-read the wallet after the Poor Box closes so the gallery + wings agree
    with the server about what is affordable. */
async function refreshWalletAfterGiving(): Promise<void> {
  if (!isAuthenticated()) return;
  const prof = await getProfileMe();
  if (prof.ok) {
    walletCents = prof.data.stats?.walletBalanceCents ?? walletCents;
    seedWingsWallet(walletCents);
    renderAll();
  }
}

/** Sign-out hook: the ladder and giving totals are per-account. */
export function resetMonasterySinks(): void {
  resetWings();
}
